import * as React from 'react';
import { useImperativeHandle } from 'react';
import { Typography, List, ListItem, ListItemText, ListItemIcon, Button, ListItemButton } from '@mui/material';
import { Box, Paper, Divider } from '@mui/material';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import VitalsForm from '../forms/VitalsForm';
import { useHistoryVitals } from '../../../hooks/history/useHistoryVitals';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import { removeZeroDecimals } from '../../../utils/removeZeroDecimals';
import VitalsView from '../modals/VitalsView';
import { useDeleteVitals } from '../../../hooks/history/useDeleteVitals';
import { getVitalsById } from '../../../api/historyService';
import { formatToLocalDateTime } from '../../../utils/dateFormatter';
import { getTempLevel } from '../../../utils/getTempLevel';
import { getPulseLevel } from '../../../utils/getPulseLevel';
import { getRespiratoryLevel } from '../../../utils/getRespiratoryLevel';

export default function VitalsListItems({ setListEmpty, setDoneLoading = null, history = null, historyEntry = null, pet = null, mode='history', ref, fetchData=null }) {
  const { t } = useTranslation('common');
  const { showSnackbar } = useSnackbar();

  const  {vitals, loading, loadMore, searchFunction, resetParent } = useHistoryVitals(history?.id, historyEntry?.id, pet?.id, mode);

  const [selectedVital, setSelectedVital] = React.useState(null);

  const [vitalsFormOpen, setVitalsFormOpen] = React.useState(false);
  const [vitalsFormMode, setVitalsFormMode] = React.useState('create');
  const [vitalsViewOpen, setVitalsViewOpen] = React.useState(false);

  const onVitalsSaveSuccess = async (updatedRecord) => {
    const updatedVitals = await getVitalsById(updatedRecord.id);
    setSelectedVital(updatedVitals);
    resetParent();
    fetchData();
  };

  const handleVitalsCreate = () => {
    setSelectedVital(null);
    setVitalsFormMode('create');
    setVitalsFormOpen(true);
  };
  // Expose the function to parent
  useImperativeHandle(ref, () => ({
    handleVitalsCreate
  }));

  const handleVitalsEdit = (vital) => {
    setSelectedVital(vital);
    setVitalsFormMode('edit');
    setVitalsFormOpen(true);
  };
  const handleVitalsDelete = async (vital) => {
    try {
      await remove(vital.id);
    } catch (err) {
      showSnackbar(t('vitals.messages.delete_fail'), "error");
    }
    
  };
  const onDeleteVitalsSuccess = () => {
    resetParent();
    showSnackbar(t('vitals.messages.delete_success'), "success");
  };
  const { remove } = useDeleteVitals(onDeleteVitalsSuccess);

  const handleListItemClick = (vital) => {
    setSelectedVital(vital);
    setVitalsViewOpen(true);
  };

  const handleListItemEdit = (vital) => {
    handleVitalsEdit(vital);
  };

  const handleListItemDelete = (vital) => {
    handleVitalsDelete(vital);
  };

  React.useEffect(() => {
    if (vitals.data.length !== 0)
    {
      setListEmpty(false);
    }
  }, [vitals]);

  React.useEffect(() => {
    if (!loading)
    {
      if (setDoneLoading) setDoneLoading(true);
    }
  }, [loading]);

  return (
    <>
      {loading ? (
        <></>
      ) : (
      vitals?.data.map((vital) => (
        <ListItem 
            key={"vitals" + vital.id}
            sx={{ py: 0 }}
            alignItems="flex-start"
            secondaryAction={
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <VerticalHamburgerMenu
                    options={[
                        { 
                            id: 1, 
                            name: 'View',
                            callback: () => handleListItemClick(vital)
                        },
                        { 
                            id: 2, 
                            name: 'Edit',
                            callback: () => handleListItemEdit(vital)
                        },
                        {
                            id: 3,
                            name: 'Delete',
                            callback: () => handleListItemDelete(vital),
                            confirmMessage: t('vitals.messages.delete_confirm')
                        }
                    ]}
                    />
                </Box>
            }
        >
            <ListItemButton 
              sx={{ py: 0 }} 
              onClick={() => handleListItemClick(vital)}
            >
            <ListItemIcon>
              <MonitorHeartIcon color='primary' />
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                <Typography component="span" variant="body2" sx={{ maxWidth: '90%' }}>
                  {`${vital.weight ? "W" + removeZeroDecimals(vital.weight) : ""} `}
                </Typography> 
                {vital.temp > 0 ?
                <Typography color={getTempLevel(vital.temp, vital.pet.species_id)} component="span" variant="body2" sx={{ maxWidth: '90%' }}>
                  {`T${removeZeroDecimals(vital.temp)} `}
                </Typography>
                : null }
                {vital.pulse > 0 ?
                <Typography color={getPulseLevel(vital.pulse, vital.pet.species_id, vital.weight, vital.pet.birthdate)} component="span" variant="body2" sx={{ maxWidth: '90%' }}>
                  {`P${removeZeroDecimals(vital.pulse)} `}
                </Typography>
                : null }
                {vital.respiration > 0 ?
                <Typography color={getRespiratoryLevel(vital.respiration, vital.pet.species_id)} component="span" variant="body2" sx={{ maxWidth: '90%' }}>
                  {`R${removeZeroDecimals(vital.respiration)} `}
                </Typography>
                : null }
                </>
              }
            secondary={formatToLocalDateTime(vital.updatedAt)}
            />
            </ListItemButton>
        </ListItem>
      )))}
      <VitalsForm
        mode={vitalsFormMode}
        vitals={selectedVital}
        successCallback={onVitalsSaveSuccess}
        history={history}
        historyEntry={historyEntry}
        pet={pet} 
        open={vitalsFormOpen}
        onClose={() => setVitalsFormOpen(false)}
      />
      <VitalsView 
        handleEdit={handleVitalsEdit}
        vitals={selectedVital}
        open={vitalsViewOpen}
        onClose={() => setVitalsViewOpen(false)}
      />
    </>
  );
}